import { Ldap, LdapContainer } from './../core/models/ldap';
import { LdapService } from './../core/services/ldap.service';
import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked, ChangeDetectorRef } from '@angular/core';
import * as _ from 'lodash';
import { BzgToastService, BzgModalService, BzgModalRef, BzgToastType } from 'bz-genome-angular';
import { TranslateService } from '@ngx-translate/core';
import { LdapBasicConfigurationComponent } from './ldap-basic-configuration/ldap-basic-configuration.component';
import { Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { ILdapTestResults } from '../core/typings/ILdapTestResults';
import { LdapTestResultsComponent } from './ldap-test-results/ldap-test-results.component';
import { IAppState } from 'app/IAppState';
import { I18nService } from 'i18n/i18n.service';
import { ShowToastService } from 'app/shared/services/showToast.service';
import { DateUtil } from 'app/core/utils/DateUtil';

@Component({
  selector: 'security-ldap',
  templateUrl: 'security-ldap.component.html',
  styleUrls: ['security-ldap.component.scss']
})
export class SecurityLdapComponent implements OnInit, AfterViewChecked {
  public readonly ERRORS_ATTRIBUTE = [
    'ContactEmailAttributeMissing',
    'FullNameAttributeMissing'
  ]

  public _ldapEnvironment: Ldap;
  public isLoadingLdap: boolean;
  public isTestingLdap: boolean;
  public testResults: ILdapTestResults;
  public isTestingResultOk: boolean;
  public userProperties: any[];
  public tabs: any;
  public selectedTab: number = 0;

  private _ldapContainer: LdapContainer;
  private ldapResult: any;
  private ldapEnvironments: Ldap[];
  private _tabsSubscription: Subscription;

  @ViewChild('ldapBasicConfiguration', { static: false }) ldapBasicConfiguration: LdapBasicConfigurationComponent;

  constructor(private ldapService: LdapService,
    private _toastService: BzgToastService,
    private _translateService: TranslateService,
    private _store: Store<IAppState>,
    private _modalService: BzgModalService,
    private cdRef: ChangeDetectorRef,
    private _i18nService: I18nService,
    private _showToastService: ShowToastService) { }

  ngOnInit() {

    this.isLoadingLdap = true;

    this._tabsSubscription = this._store
      .select((s) => s.menuOptions)
      .subscribe((menuOptions: any) => {
        this.tabs = menuOptions['Ldap'];
      });

    const currentEnvironment = sessionStorage.getItem('environmentType');

    this.ldapService.getLdapConfiguration().subscribe((response: any) => {
      this.isLoadingLdap = false;
      this.ldapResult = response;

      this._ldapContainer = response[0].content;
      this.ldapEnvironments = JSON.parse(this._ldapContainer.environments);
      this._ldapEnvironment = _.find(this.ldapEnvironments, { 'environment': currentEnvironment });
    }, (error) => {
      this.isLoadingLdap = false;
      this._showToastService.showToast(this._i18nService.instant('GLOBAL.AN_ERROR_HAS_OCCURRED'), 'error');
    });

    this.loadUserProperties();
  }

  ngAfterViewChecked() {
    this.cdRef.detectChanges();
  }

  ngOnDestroy() {
    this._tabsSubscription.unsubscribe();
  }

  setSynchDataValue(showOption) {
    const currentEnvironment = sessionStorage.getItem('environmentType');
    let syncHour = 0;
    if (currentEnvironment) {
      const environmentObject = this.ldapEnvironments.find(env => env.environment === currentEnvironment);
      if (environmentObject) {
        environmentObject.synchHour = DateUtil.calculateHoursToShowOrSave(environmentObject.synchHour, showOption);
        syncHour = environmentObject.synchHour;
      }
    }
  }

  public saveData() {
    if (this.ldapBasicConfiguration.isValid()) {
      this.setSynchDataValue(false);
      const environments = { environments: JSON.stringify(this.ldapEnvironments) };
      this.ldapResult[0].content = environments;
      this.setSynchDataValue(true);

      this.ldapService.putLdap(this.ldapResult).subscribe((response) => {
        if (response.save) {
          this._showToastService.showToast(this._i18nService.instant('LDAP.SAVED_SUCCESSFULLY'), 'success');
        } else if (response.error) {
          if (this.ERRORS_ATTRIBUTE.indexOf(response.error) > -1) {
            const message = 'LDAP.ERROR_ATTRIBUTE_' + response.error.toUpperCase();
            this._showToastService.showToast(this._i18nService.instant(message), 'error');
          }
        }
      }, (error) => {
        this.isLoadingLdap = false;
        this._showToastService.showToast(this._i18nService.instant('GLOBAL.AN_ERROR_HAS_OCCURRED'), 'error');
      });
    }
    else {
      this._showToastService.showToast(this._i18nService.instant('LDAP.BASIC_INVALID_VALUES_MESSAGE'), 'error');
    }
  }

  public testConnection() {
    if (this.ldapBasicConfiguration.isValid()) {
      this.isTestingLdap = true;
      const environment = JSON.stringify(this._ldapEnvironment);
      this.ldapService.testConnection(environment).subscribe((response) => {
        this.isTestingLdap = false;
        this.testResults = response;
        if (response.status === 'Success') {
          this.isTestingResultOk = true;
          this._showToastService.showToast(this._i18nService.instant('LDAP.SUCCESSFUL_TEST'), 'success');
        } else if (response.status === 'UsersNotFound') {
          this.isTestingResultOk = false;
          this._showToastService.showToast(this._i18nService.instant('LDAP.ERROR_LDAP_USER_NOT_FOUND'), 'error');
        } else {
          let description: string = 'LDAP.ERROR_LDAP_CONFIGURATION';
          if (response.description)
            description = response.description.startsWith('LDAP error.') ? 'LDAP.ERROR_LDAP_CONFIGURATION' : response.description;
          this.isTestingResultOk = false;
          this._showToastService.showToast(this._i18nService.instant(description), 'error');
        }
      }, (error) => {
        this.isTestingLdap = false;
        this.isTestingResultOk = false;
        this._showToastService.showToast(this._i18nService.instant('GLOBAL.AN_ERROR_HAS_OCCURRED'), 'error');
      });
    }
    else {
      this._showToastService.showToast(this._i18nService.instant('LDAP.BASIC_INVALID_VALUES_MESSAGE'), 'error');
    }
  }

  public viewTestResults() {

    this._modalService.open(LdapTestResultsComponent, {
      closeOnBackdropClick: false,
      closeOnRouterNavigation: true,
      width: '90%',
      height: '90%',
      maxWidth: '90%',
      data: {
        ldapEnvironment: this._ldapEnvironment,
        testResults: this.testResults,
      }
    });
  }

  private loadUserProperties() {
    this.ldapService.getUserProperties().subscribe((response) => {
      this.isLoadingLdap = false;
      this.userProperties = [];
      const _userProperties = response;
      _.forEach(_userProperties, (each) => {
        let valueLower = each.name;
        this.userProperties.push({ value: valueLower });
      });
    }, (error) => {
      this.isLoadingLdap = false;
      this._showToastService.showToast(this._i18nService.instant('GLOBAL.AN_ERROR_HAS_OCCURRED'), 'error');
    });
  }
}
